/**
 * Data layer for `advertiser-api-v2`. Thin typed fetch wrappers over
 * /api/v1/advertiser-api-v2. Raw `fetch()` is confined to this file per
 * the dash contract.
 *
 * BE surface (admin, tenant-scoped):
 *   GET    /api/v1/advertiser-api-v2/keys?advertiser_id=...
 *   POST   /api/v1/advertiser-api-v2/keys
 *   DELETE /api/v1/advertiser-api-v2/keys/:id
 *   GET    /api/v1/advertiser-api-v2/access-log?advertiser_id=...&limit=N
 */

export const V2_SCOPES = [
	"conversions.write",
	"reports.read",
	"invoices.read",
] as const;
export type AdvertiserV2Scope = (typeof V2_SCOPES)[number];

export const V2_SCOPE_DESCRIPTIONS: Record<AdvertiserV2Scope, string> = {
	"conversions.write": "Push conversion events and run dry-run validation.",
	"reports.read": "Read campaign and publisher reports for the advertiser.",
	"invoices.read": "List advertiser invoice PDFs.",
};

export interface AdvertiserV2KeyMasked {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	name: string;
	key_preview: string;
	scopes: AdvertiserV2Scope[];
	active: boolean;
	created_at: number;
	revoked_at: number | null;
}

export interface ListKeysResponse {
	advertiser_id: string;
	count: number;
	keys: AdvertiserV2KeyMasked[];
}

export interface CreateKeyBody {
	advertiser_id: string;
	name: string;
	scopes: AdvertiserV2Scope[];
}

export interface CreateKeyResponse {
	key: AdvertiserV2KeyMasked;
	raw_key: string;
}

export interface RevokeKeyResponse {
	id: string;
	revoked_at: number;
	revoked: boolean;
}

export interface AccessLogRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	key_id: string;
	method: string;
	path: string;
	status_code: number;
	ts: number;
}

export interface ListAccessLogResponse {
	advertiser_id: string;
	count: number;
	limit: number;
	entries: AccessLogRow[];
}

const BASE = "/api/v1/advertiser-api-v2";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listKeys(advertiserId: string): Promise<ListKeysResponse> {
	const qs = new URLSearchParams({ advertiser_id: advertiserId }).toString();
	const res = await fetch(`${BASE}/keys?${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListKeysResponse>(res);
}

export async function createKey(body: CreateKeyBody): Promise<CreateKeyResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateKeyResponse>(res);
}

export async function revokeKey(id: string): Promise<RevokeKeyResponse> {
	const res = await fetch(`${BASE}/keys/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RevokeKeyResponse>(res);
}

export async function listAccessLog(
	advertiserId: string,
	limit: number = 100,
): Promise<ListAccessLogResponse> {
	const qs = new URLSearchParams({
		advertiser_id: advertiserId,
		limit: String(limit),
	}).toString();
	const res = await fetch(`${BASE}/access-log?${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAccessLogResponse>(res);
}

export function toggleScope(
	current: readonly AdvertiserV2Scope[],
	scope: AdvertiserV2Scope,
): AdvertiserV2Scope[] {
	if (current.includes(scope)) return current.filter((s) => s !== scope);
	return [...current, scope];
}

export const advertiserApiV2QueryKeys = {
	all: ["advertiser-api-v2"] as const,
	keys: (advertiserId: string) =>
		[...advertiserApiV2QueryKeys.all, "keys", advertiserId] as const,
	accessLog: (advertiserId: string, limit: number) =>
		[
			...advertiserApiV2QueryKeys.all,
			"access-log",
			advertiserId,
			limit,
		] as const,
	detail: (id: string) => [...advertiserApiV2QueryKeys.all, id] as const,
};
